// The Cmd/Ctrl+K command palette (Views/Shared/_CommandPalette.cshtml).
//
// With nothing typed it offers quick actions. As you type it filters those, the
// sidebar's pages and the site picker's sites in the browser, and asks
// /palette/search (Features/CommandPalette) for matching jobs and recruiters.
// Arrow keys move, Enter opens (Cmd/Ctrl+Enter in a new tab), Esc closes.

const isMac = /Mac|iPhone|iPad/.test(navigator.userAgent);
const SEARCH_DELAY = 180;
const MIN_SEARCH_LENGTH = 2;
const PAGE_LIMIT = 6;
const RESULT_LIMIT = 6; // MaxResultsPerGroup in CommandPaletteController

const dateFormat = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

function formatRange(start, end) {
  return `${dateFormat.format(new Date(start))} – ${dateFormat.format(new Date(end))}`;
}

function text(element) {
  return element.textContent.replace(/\s+/g, ' ').trim();
}

// Pages come from the sidebar, so the palette always matches the menu.
function readPages() {
  return [...document.querySelectorAll('#sidebar a.sidebar-link[href], #sidebar a.sidebar-sublink[href]')]
    .filter((link) => link.id !== 'logout')
    .map((link) => {
      const parent = link.closest('.sidebar-submenu')?.previousElementSibling;
      const section = parent ? text(parent) : '';

      return {
        label: text(link),
        meta: section,
        href: link.getAttribute('href'),
        icon: link.querySelector('use')?.getAttribute('href')?.split('#')[1]
          ?? parent?.querySelector('use')?.getAttribute('href')?.split('#')[1]
          ?? 'arrow-right',
        keywords: section.toLowerCase(),
      };
    });
}

// Sites come from the header's site picker, current one marked, each with its colour dot.
function readSites() {
  return [...document.querySelectorAll('header a[href*="choosesite" i]')].map((link) => ({
    label: text(link),
    meta: link.getAttribute('aria-current') === 'true' ? 'Current site' : 'Switch to this site',
    href: link.getAttribute('href'),
    icon: 'globe-alt',
    dot: link.querySelector('.site-dot')?.getAttribute('style') ?? '',
    keywords: 'site switch change',
  }));
}

export default () => ({
  open: false,
  query: '',
  active: 0,
  loading: false,
  failed: false,
  results: { jobs: [], recruiters: [] },
  shortcut: isMac ? '⌘K' : 'Ctrl K',

  actions: [],
  pages: [],
  sites: [],
  cache: new Map(),
  timer: null,
  controller: null,

  init() {
    // Quick actions are rendered server-side (tag helpers build the URLs).
    this.actions = [...this.$refs.actions.querySelectorAll('a')].map((link) => ({
      label: text(link),
      meta: link.dataset.meta ?? '',
      href: link.getAttribute('href'),
      icon: link.dataset.icon,
      keywords: (link.dataset.keywords ?? '').toLowerCase(),
    }));
    this.pages = readPages();
    this.sites = readSites();

    window.addEventListener('keydown', (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        this.open ? this.close() : this.show();
        return;
      }

      // Keys typed straight after Cmd+K can land before the input has focus; keep them.
      const typing = event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey;

      if (this.open && typing && document.activeElement !== this.$refs.input) {
        event.preventDefault();
        this.query += event.key;
        this.$refs.input.focus();
      }
    });

    this.$watch('query', () => {
      this.active = 0;
      this.scheduleSearch();
    });
  },

  show() {
    this.open = true;
    this.$nextTick(() => {
      this.$refs.input.focus();
      this.$refs.input.select();
    });
  },

  close() {
    this.open = false;
    this.controller?.abort();
  },

  get needle() {
    return this.query.trim().toLowerCase();
  },

  matches(item) {
    const needle = this.needle;
    return !needle || item.label.toLowerCase().includes(needle) || item.keywords?.includes(needle);
  },

  scheduleSearch() {
    clearTimeout(this.timer);
    this.controller?.abort();
    this.failed = false;

    const term = this.needle;

    if (term.length < MIN_SEARCH_LENGTH) {
      this.loading = false;
      this.results = { jobs: [], recruiters: [] };
      return;
    }

    if (this.cache.has(term)) {
      this.loading = false;
      this.results = this.cache.get(term);
      return;
    }

    this.loading = true;
    this.timer = setTimeout(() => this.search(term), SEARCH_DELAY);
  },

  async search(term) {
    this.controller = new AbortController();

    try {
      const response = await fetch(`/palette/search?${new URLSearchParams({ q: term })}`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        signal: this.controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`);
      }

      const results = await response.json();
      this.cache.set(term, results);

      if (term === this.needle) {
        this.results = results;
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.failed = true;
        this.results = { jobs: [], recruiters: [] };
      }
    } finally {
      if (term === this.needle) {
        this.loading = false;
      }
    }
  },

  // Everything shown, grouped, with a running index for keyboard focus.
  get groups() {
    const groups = [];
    const searching = this.needle.length > 0;
    const add = (key, label, items) => {
      if (items.length) {
        groups.push({ key, label, items });
      }
    };

    add('actions', searching ? 'Actions' : 'Quick actions', this.actions.concat(this.themeActions(searching)).filter((item) => this.matches(item)));

    // Local matches first: they're instant, so they don't jump when search results arrive.
    if (searching) {
      add('pages', 'Pages', this.pages.filter((item) => this.matches(item)).slice(0, PAGE_LIMIT));
      add('sites', 'Switch site', this.sites.filter((item) => this.matches(item)));
    }

    const { jobs = [], recruiters = [], jobsUrl, recruitersUrl } = this.results;

    add('recruiters', 'Recruiters', recruiters.map((recruiter) => ({
      label: recruiter.name,
      meta: recruiter.town ?? '',
      href: recruiter.url,
      icon: 'building-office-2',
      badge: recruiter.isActive ? 'Active' : 'Inactive',
      tone: recruiter.isActive ? 'accent' : 'neutral',
    })).concat(recruiters.length === RESULT_LIMIT && recruitersUrl ? [{
      label: `See all recruiters matching “${this.query.trim()}”`, href: recruitersUrl, icon: 'magnifying-glass', subtle: true,
    }] : []));

    add('jobs', 'Jobs', jobs.map((job) => ({
      label: job.title,
      meta: [job.recruiterName, formatRange(job.startDate, job.endDate)].filter(Boolean).join(' · '),
      href: job.url,
      icon: 'briefcase',
      badge: job.isActive ? 'Live' : 'Inactive',
      tone: job.isActive ? 'accent' : 'neutral',
    })).concat(jobs.length === RESULT_LIMIT && jobsUrl ? [{
      label: `See all jobs matching “${this.query.trim()}”`, href: jobsUrl, icon: 'magnifying-glass', subtle: true,
    }] : []));

    let index = 0;
    groups.forEach((group) => {
      group.items = group.items.map((item) => ({ ...item, index: index++, key: `${group.key}:${item.href}:${item.label}` }));
    });

    return groups;
  },

  // Run in place rather than opening a page (Assets/js/theme.js).
  themeActions(searching) {
    const theme = this.$store.theme;
    const actions = [{
      label: theme.dark ? 'Switch to light mode' : 'Switch to dark mode',
      meta: 'Theme',
      icon: theme.dark ? 'sun' : 'moon',
      keywords: 'theme dark light mode appearance night',
      run: () => theme.toggle(),
    }];

    if (searching && theme.choice !== 'system') {
      actions.push({
        label: 'Match system theme',
        meta: 'Follow your device\'s light or dark setting',
        icon: 'computer-desktop',
        keywords: 'theme system auto device dark light mode appearance',
        run: () => theme.set('system'),
      });
    }

    return actions;
  },

  get items() {
    return this.groups.flatMap((group) => group.items);
  },

  get activeId() {
    return this.items.length ? `palette-item-${this.active}` : null;
  },

  get isEmpty() {
    return this.needle.length > 0 && !this.loading && this.items.length === 0;
  },

  move(step) {
    const count = this.items.length;

    if (!count) {
      return;
    }

    this.active = (this.active + step + count) % count;
    this.$nextTick(() => document.getElementById(this.activeId)?.scrollIntoView({ block: 'nearest' }));
  },

  go(item, event) {
    if (item?.run) {
      item.run();
      this.close();
      return;
    }

    if (!item?.href) {
      return;
    }

    if (event?.metaKey || event?.ctrlKey) {
      window.open(item.href, '_blank', 'noopener');
      return;
    }

    window.location.href = item.href;
  },

  onKeydown(event) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.move(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.move(-1);
        break;
      case 'Enter':
        event.preventDefault();
        this.go(this.items[this.active], event);
        break;
      case 'Escape':
        event.preventDefault();
        this.close();
        break;
      default:
    }
  },
});
